/**
 * PostHog thin wrapper — safe no-op when VITE_POSTHOG_KEY is missing.
 *
 * Runs alongside existing Meta Pixel / GA4; never throws.
 * Loads posthog-js lazily on first interaction or 4s after mount, whichever
 * comes first — matches existing GA4 / Pixel deferred-loading strategy so
 * we don't hurt LCP.
 */

type PhModule = typeof import('posthog-js').default;

const KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined) || '';
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  'https://us.i.posthog.com';

const ENABLED = typeof window !== 'undefined' && !!KEY;

type QueueItem =
  | { type: 'capture'; event: string; props?: Record<string, any> }
  | { type: 'identify'; id: string; props?: Record<string, any> }
  | { type: 'reset' }
  | { type: 'pageview'; path: string }
  | { type: 'disable_recording' }
  | { type: 'enable_recording' };

let loadPromise: Promise<PhModule | null> | null = null;
let ph: PhModule | null = null;
const queue: QueueItem[] = [];

function flush() {
  if (!ph) return;
  while (queue.length) {
    const item = queue.shift()!;
    try {
      switch (item.type) {
        case 'capture':
          ph.capture(item.event, item.props);
          break;
        case 'identify':
          ph.identify(item.id, item.props);
          break;
        case 'reset':
          ph.reset();
          break;
        case 'pageview':
          ph.capture('$pageview', { $current_url: window.location.href });
          break;
        case 'disable_recording':
          (ph as any).stopSessionRecording?.();
          break;
        case 'enable_recording':
          (ph as any).startSessionRecording?.();
          break;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.debug('[posthog] flush error', err);
    }
  }
}

function loadOnce(): Promise<PhModule | null> {
  if (!ENABLED) return Promise.resolve(null);
  if (loadPromise) return loadPromise;
  loadPromise = import('posthog-js')
    .then((mod) => {
      const instance = mod.default;
      instance.init(KEY, {
        api_host: HOST,
        capture_pageview: false,
        capture_pageleave: true,
        person_profiles: 'identified_only',
        session_recording: {
          maskAllInputs: true,
          maskInputOptions: {
            password: true,
            email: true,
            tel: true,
          },
          maskTextSelector: '[data-ph-mask], .ph-mask',
        },
        loaded: (posthog) => {
          ph = posthog as unknown as PhModule;
          flush();
        },
      });
      return instance;
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.debug('[posthog] load failed', err);
      return null;
    });
  return loadPromise;
}

function scheduleLoad() {
  if (!ENABLED || typeof window === 'undefined') return;
  let triggered = false;
  const trigger = () => {
    if (triggered) return;
    triggered = true;
    cleanup();
    void loadOnce();
  };
  const events: Array<keyof WindowEventMap> = [
    'pointerdown',
    'scroll',
    'keydown',
    'touchstart',
  ];
  const cleanup = () => {
    events.forEach((ev) =>
      window.removeEventListener(ev, trigger, { capture: true } as any)
    );
  };
  events.forEach((ev) =>
    window.addEventListener(ev, trigger, { once: true, capture: true, passive: true } as any)
  );
  window.setTimeout(trigger, 4000);
}

if (ENABLED) scheduleLoad();

function safe(item: QueueItem) {
  if (!ENABLED) return;
  if (ph) {
    queue.push(item);
    flush();
  } else {
    queue.push(item);
    void loadOnce();
  }
}

export function capture(event: string, props?: Record<string, any>) {
  try {
    safe({ type: 'capture', event, props });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] capture error', err);
  }
}

export function identify(id: string, props?: Record<string, any>) {
  try {
    safe({ type: 'identify', id, props });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] identify error', err);
  }
}

export function reset() {
  try {
    safe({ type: 'reset' });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] reset error', err);
  }
}

export function capturePageview(_path?: string) {
  try {
    safe({ type: 'pageview', path: _path || (typeof window !== 'undefined' ? window.location.pathname : '/') });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[posthog] pageview error', err);
  }
}

export function disableSessionRecording() {
  try {
    safe({ type: 'disable_recording' });
  } catch { /* noop */ }
}

export function enableSessionRecording() {
  try {
    safe({ type: 'enable_recording' });
  } catch { /* noop */ }
}

/**
 * Returns current PostHog distinct_id if the SDK is loaded, else null.
 * Used at checkout to associate a server-side purchase event back to the
 * same visitor's session (see supabase/functions/payuni-notify/index.ts).
 */
export function getDistinctId(): string | null {
  if (!ENABLED || !ph) return null;
  try {
    return ph.get_distinct_id?.() || null;
  } catch {
    return null;
  }
}

export const posthogEnabled = ENABLED;
